import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from 'react'
import Script from 'next/script'

interface SurfboardPhone {
  countryCode: string
  number: string
}

interface SurfboardBillingAddress {
  addressId?: string
  careOf?: string
  addressLine1: string
  addressLine2?: string
  addressLine3?: string
  city: string
  countryCode: string
  postalCode: string
}

interface SurfboardTax {
  type: 'VAT'
  percentage: number
  amount: number
}

interface SurfboardItemPrice {
  value: string
  currency: string
  campaign: string
  regular: string
  tax: SurfboardTax[]
}

interface SurfboardOrderLine {
  externalItemId: string
  gtin: string
  brand: string
  itemPrice: SurfboardItemPrice
  itemId: string
  quantity: number
  unit: string
  name: string
  description?: string
  imageUrl?: string
}

interface SurfboardOrder {
  orderId: string
  terminalId: string
  billingAddress: SurfboardBillingAddress
  orderStatus: string
  orderDate: string
  memberId: string
  totalOrderPriceValue: string
  totalOrderPriceCurrency: string
  memberEmail: string
  memberPhone: SurfboardPhone
  referenceId: string
  orderLines: SurfboardOrderLine[]
  tax: SurfboardTax[]
}

interface SurfboardCustomerInfo {
  phone: SurfboardPhone
  name: string
  email: string
  billingAddress: SurfboardBillingAddress
}

type SurfboardPaymentStatus =
  | 'PENDING'
  | 'PAYMENT_INITIATED'
  | 'PAYMENT_CANCELLED'
  | 'PAYMENT_COMPLETED'
  | 'PAYMENT_FAILED'
  | 'PAYMENT_PROCESSING'
  | 'PAYMENT_PROCESSED'

export type { SurfboardPaymentStatus }

interface SurfboardPaymentStatusCallbackData {
  paymentStatus: SurfboardPaymentStatus
  failureReason?: string
}

type SurfboardPaymentMethod =
  | 'CARD'
  | 'APPLE_PAY'
  | 'GOOGLE_PAY'
  | 'KLARNA'
  | 'NSWISH'

export type { SurfboardPaymentMethod }

interface SurfboardPaymentMethods {
  supportedPaymentMethods: SurfboardPaymentMethod[]
  lockToPaymentMethods: SurfboardPaymentMethod[] | null
}

interface SurfboardMerchant {
  name: string
  organizationNumber: string
}

interface SurfboardBranding {
  backgroundColor: string
  brandColor: string
  accentColor: string
  rectShape: 'EDGY' | 'ROUNDED' | 'PILL'
  fontType: 'serif' | 'sans-serif' | 'monospace'
  logoUrl: string
  iconUrl: string
}

interface SurfboardStore {
  name: string
  phoneNumber: SurfboardPhone
  email: string
  address: SurfboardBillingAddress
  privacyPolicyUrl: string
  termAndConditionUrl: string
}

interface SurfboardCustomer {
  name?: string
  email?: string
  phone?: SurfboardPhone
  orderBillingAddress?: SurfboardBillingAddress
  customerId?: string
  savedAddresses?: SurfboardBillingAddress[]
}

type GetPaymentStatus = {
  paymentStatus: SurfboardPaymentStatus
}

export interface PaymentAttempt {
  paymentId: string
  getPaymentStatus(): GetPaymentStatus
  cancelPayment(): void
  getSwishAppRedirectUrl(callback?: string): string
  getSwishQRData: string | null
}

type SurfboardErrorCallback = (
  callback: (code: string, message: string) => void
) => void
type SurfboardPaymentStatusCallback = (
  callback: (data: SurfboardPaymentStatusCallbackData) => void
) => void
type SurfboardMountCallback = (config: Record<string, string>) => void
type SurfboardAddCustomerInformation = (
  customerInfo: SurfboardCustomerInfo
) => Promise<boolean>

// Types
interface InternalSurfboardContextType {
  setErrorCallback: SurfboardErrorCallback
  setPaymentStatusCallback: SurfboardPaymentStatusCallback
  mount: SurfboardMountCallback
  isReady: boolean
  paymentMethods: SurfboardPaymentMethods
  merchant: SurfboardMerchant
  branding: SurfboardBranding
  store: SurfboardStore
  customer: SurfboardCustomer
  order: {
    order: SurfboardOrder
    addCustomerInformation: SurfboardAddCustomerInformation
    initiatePayments: (paymentMethod: string) => Promise<PaymentAttempt>
  }
  addCustomerInformationOnce: SurfboardAddCustomerInformation
  supportedPaymentMethods: SurfboardPaymentMethod[]
  initiatePayments: (method: string) => Promise<PaymentAttempt>
}

interface SurfboardContextType {
  supportedPaymentMethods: SurfboardPaymentMethod[]
  initiatePayments: (method: string) => Promise<PaymentAttempt>
  setErrorCallback: SurfboardErrorCallback
  setPaymentStatusCallback: SurfboardPaymentStatusCallback
  addCustomerInformationOnce: SurfboardAddCustomerInformation
  isReady: boolean
}

interface SurfboardProviderProps {
  publicKey: string
  orderId: string
  nonce: string
  children: React.ReactNode
  options?: {
    locale?: string
  }
}

interface PaymentElementProps {
  type: SurfboardPaymentMethod
}

// Create context
const SurfboardContext = createContext<InternalSurfboardContextType | null>(
  null
)

// Custom hooks
export const useSurfboard = (): SurfboardContextType => {
  const context = useContext(SurfboardContext)
  if (!context) {
    throw new Error('useSurfboard must be used within a SurfboardProvider')
  }
  return {
    supportedPaymentMethods: context.supportedPaymentMethods,
    initiatePayments: context.initiatePayments,
    setErrorCallback: context.setErrorCallback,
    setPaymentStatusCallback: context.setPaymentStatusCallback,
    addCustomerInformationOnce: context.addCustomerInformationOnce,
    isReady: context.isReady,
  }
}

// Custom hooks
const useInternalSurfboard = (): InternalSurfboardContextType => {
  const context = useContext(SurfboardContext)
  if (!context) {
    throw new Error('useSurfboard must be used within a SurfboardProvider')
  }
  return context
}

const addLog = (
  message: string,
  level: 'info' | 'error' | 'success' = 'info'
) => {
  const timestamp = new Date().toISOString()
  const formattedMessage = `[${level.toUpperCase()}] ${timestamp}: ${message}`
  console.log(formattedMessage)
  if (level === 'error') {
    console.error(formattedMessage)
  }
}

export const SurfboardProvider: React.FC<SurfboardProviderProps> = ({
  publicKey,
  orderId,
  nonce,
  children,
  options = {},
}) => {
  const [surfboardSDK, setSurfboardSDK] = useState<any>(null)
  const [scriptLoaded, setScriptLoaded] = useState(false)
  const [isReady, setIsReady] = useState(false)
  const [errorCallbackSet, setErrorCallbackSet] = useState(false)
  const [paymentStatusCallbackSet, setPaymentStatusCallbackSet] =
    useState(false)
  const [customerInfoSet, setCustomerInfoSet] = useState(false)

  // Update callback setters to check if they've already been set
  const setErrorCallback = useCallback(
    (callback: (code: string, message: string) => void) => {
      if (!surfboardSDK || errorCallbackSet) return
      surfboardSDK.errorCallback(callback)
      setErrorCallbackSet(true)
    },
    [surfboardSDK, errorCallbackSet]
  )

  const setPaymentStatusCallback = useCallback(
    (callback: (data: SurfboardPaymentStatusCallbackData) => void) => {
      if (!surfboardSDK || paymentStatusCallbackSet) return
      surfboardSDK.paymentStatusCallback(callback)
      setPaymentStatusCallbackSet(true)
    },
    [surfboardSDK, paymentStatusCallbackSet]
  )

  // Add new function to handle customer information
  const addCustomerInformationOnce =
    useCallback<SurfboardAddCustomerInformation>(
      async (customerInfo) => {
        if (!surfboardSDK || !isReady || customerInfoSet) return false
        try {
          await surfboardSDK.order.addCustomerInformation(customerInfo)
          setCustomerInfoSet(true)
          addLog('Customer information added successfully', 'success')
          return true
        } catch (error) {
          addLog(`Failed to add customer information: ${error}`, 'error')
          return false
        }
      },
      [surfboardSDK, isReady, customerInfoSet]
    )

  // Handle script load event
  const handleScriptLoad = () => {
    setScriptLoaded(true)
    addLog('Surfboard SDK script loaded successfully', 'success')
  }

  // Handle script error
  const handleScriptError = () => {
    addLog('Failed to load Surfboard SDK script', 'error')
  }

  useEffect(() => {
    if (!scriptLoaded) return

    const initializeSurfboard = async () => {
      if (typeof window === 'undefined') return

      try {
        // Wait for SDK to load with retry mechanism
        let retryCount = 0
        while (!(window as any).SurfboardOnlineSDK) {
          if (retryCount > 10) {
            throw new Error(
              'SurfboardOnlineSDK not available after maximum retries'
            )
          }
          await new Promise((resolve) => setTimeout(resolve, 500))
          retryCount++
        }

        const SDK = (window as any).SurfboardOnlineSDK
        addLog('Starting Surfboard SDK initialization')

        // Initialize SDK
        const config = {
          publicKey,
          orderId,
          nonce,
        }
        addLog(`SDK configuration: ${JSON.stringify(config)}`)

        await SDK.initialiseOnlineSDK(config)
        addLog('SDK initialized successfully')

        if (options.locale) {
          SDK.changeLocale(options.locale)
          addLog(`Locale changed to: ${options.locale}`)
        }

        setSurfboardSDK(SDK)
        setIsReady(true)
        addLog('Surfboard instance stored in context', 'success')
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Unknown error'
        addLog(`Failed to initialize Surfboard SDK: ${errorMessage}`, 'error')
        console.error('Failed to initialize Surfboard SDK:', error)
        setIsReady(false)
      }
    }

    initializeSurfboard()
  }, [scriptLoaded, publicKey, orderId, nonce, options.locale])

  return (
    <>
      {/* Add CSP meta tag */}
      <meta
        httpEquiv="Content-Security-Policy"
        content="default-src * 'unsafe-inline' 'unsafe-eval'; script-src * 'unsafe-inline' 'unsafe-eval'; connect-src * 'unsafe-inline'; img-src * data: blob: 'unsafe-inline'; frame-src * threeds.beta.surfboard.se; style-src * 'unsafe-inline';"
      />

      {/* Load Surfboard SDK script */}
      <Script
        src="https://thorium.surfgw.com/sdk/online/v1/surfboard-online-sdk.js"
        onLoad={handleScriptLoad}
        onError={handleScriptError}
        strategy="lazyOnload"
      />

      <SurfboardContext.Provider
        value={{
          setErrorCallback,
          setPaymentStatusCallback,
          isReady,
          paymentMethods: surfboardSDK?.paymentMethods,
          order: surfboardSDK?.order,
          mount: surfboardSDK?.mount,
          merchant: surfboardSDK?.merchant,
          branding: surfboardSDK?.branding,
          store: surfboardSDK?.store,
          customer: surfboardSDK?.customer,
          addCustomerInformationOnce,
          supportedPaymentMethods:
            surfboardSDK?.paymentMethods?.supportedPaymentMethods || [],
          initiatePayments: (method: string) =>
            surfboardSDK?.order?.initiatePayments(method),
        }}
      >
        {children}
      </SurfboardContext.Provider>
    </>
  )
}

// Add a custom hook for payment methods
export const useSurfboardPaymentMethods = () => {
  const { supportedPaymentMethods, isReady } = useSurfboard()
  const [supportedMethods, setSupportedMethods] = useState<string[]>([])

  useEffect(() => {
    if (!isReady) return
    setSupportedMethods(supportedPaymentMethods)
  }, [supportedPaymentMethods, isReady])

  return supportedMethods
}

// Update PaymentElement to handle QR codes for Swish
export const PaymentElement: React.FC<PaymentElementProps> = ({ type }) => {
  const elementRef = React.useRef<HTMLDivElement>(null)
  const { mount, isReady, initiatePayments } = useInternalSurfboard()
  const [qrData, setQrData] = useState<string | null>(null)
  const [isMounted, setIsMounted] = useState(false)

  useEffect(() => {
    if (!elementRef.current || !mount || !isReady || isMounted) return

    const mountConfig: Record<string, string> = {}
    switch (type) {
      case 'CARD':
        mountConfig.mountCardWidget = elementRef.current.id
        break
      case 'APPLE_PAY':
        mountConfig.mountApplePayWidget = elementRef.current.id
        break
      case 'GOOGLE_PAY':
        mountConfig.mountGooglePayWidget = elementRef.current.id
        break
      case 'KLARNA':
        mountConfig.mountKlarnaWidget = elementRef.current.id
        break
    }

    mount(mountConfig)
    setIsMounted(true)

    // Handle Swish QR code for desktop
    if (
      type === 'NSWISH' &&
      !/Android|iPhone|iPad|iPod/i.test(navigator.userAgent)
    ) {
      initiatePayments('NSWISH').then((response: PaymentAttempt) => {
        if (response.getSwishQRData) {
          setQrData(response.getSwishQRData)
        }
      })
    }
  }, [mount, type, isReady, isMounted, initiatePayments])

  if (type === 'NSWISH' && qrData) {
    return (
      <div className="rounded-lg bg-white p-6">
        <h3 className="mb-4 text-lg font-medium">Scan with Swish app</h3>
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img src={qrData} alt="Swish QR Code" className="mx-auto h-64 w-64" />
      </div>
    )
  }

  return <div ref={elementRef} id={`surfboard-${type.toLowerCase()}`} />
}
